import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import "react-phone-input-2/lib/style.css";
import BreadCrumb from "../../components/common/BreadCrumb";
import { GlobalContext } from "../../context/Provider";
import henceforthApi, { GOOGLE_API_KEY } from "../../utils/henceforthApi";
import { toast } from "react-toastify";

const AddStore = () => {
    const navigate = useNavigate();
    const placeInputRef = useRef<HTMLInputElement>(null);

    const { language_data, loading, setLoading, authState } = useContext(GlobalContext);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
        country_code: "220",
        phone_number: "",
        company: "",
        country: "",
        state: "",
        city: "",
        pin_code: "",
        apartment_number: "",
        full_address: "",
        latitude: "",
        longitude: ""
    });

    // Stores the full E.164 number (e.g. "+12025551234") for validation
    const [rawPhone, setRawPhone] = useState("");

    const breadCrumbPath = [
        { name: `Home`, url: `/`, active: "" },
        { name: `Store Listing`, url: `/store`, active: "" },
        { name: `Add Store`, url: ``, active: "not-allowed" }
    ];

    // ─── Google Places ────────────────────────────────────────────────────────
    function loadGoogleMapScript(callback: () => void) {
        if (
            typeof (window as any).google === "object" &&
            typeof (window as any).google.maps === "object"
        ) {
            callback();
        } else {
            const script = document.createElement("script");
            script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
            window.document.body.appendChild(script);
            script.addEventListener("load", callback);
        }
    }

    const initPlaceAPI = () => {
        loadGoogleMapScript(() => {
            if (!placeInputRef.current) return;

            const autocomplete = new (window as any).google.maps.places.Autocomplete(
                placeInputRef.current
            );

            autocomplete.addListener("place_changed", () => {
                const place = autocomplete.getPlace();
                if (!place.geometry) return;

                const addressComponents = place.address_components;
                const items: any = {
                    full_address: place.formatted_address,
                    latitude: place.geometry?.location?.lat()?.toString() || "",
                    longitude: place.geometry?.location?.lng()?.toString() || ""
                };

                if (Array.isArray(addressComponents)) {
                    addressComponents.forEach((comp: any) => {
                        if (comp.types.includes("postal_code"))
                            items.pin_code = comp.long_name;
                        if (comp.types.includes("administrative_area_level_1"))
                            items.state = comp.long_name;
                        if (
                            comp.types.includes("locality") ||
                            comp.types.includes("sublocality_level_1")
                        )
                            items.city = comp.long_name;
                        if (comp.types.includes("country"))
                            items.country = comp.long_name;
                        if (
                            comp.types.includes("street_number") ||
                            comp.types.includes("premise")
                        )
                            items.apartment_number = comp.long_name;
                    });
                }

                setFormData((prev) => ({ ...prev, ...items }));
            });
        });
    };

    useEffect(() => {
        initPlaceAPI();
    }, []);

    // ─── Handlers ─────────────────────────────────────────────────────────────
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    /**
     * react-phone-input-2 callback:
     *  value  – digits only with country dial code prefix, e.g. "12025551234"
     *  data   – { dialCode, countryCode, ... }
     */
    const handlePhoneChange = (
        value: string,
        data: { dialCode: string; countryCode: string }
    ) => {
        const dialCode = data.dialCode ?? "";
        const nationalNumber = value.slice(dialCode.length); // strip dial code

        // Build E.164 for libphonenumber-js validation
        const e164 = `+${value}`;
        setRawPhone(e164);

        setFormData((prev) => ({
            ...prev,
            country_code: dialCode,
            phone_number: nationalNumber
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim()) return toast.warn("Store name is required");
        if (!formData.email.trim()) return toast.warn("Email is required");
        if (!formData.password.trim()) return toast.warn("Password is required");

        // Phone validation via libphonenumber-js
        if (!rawPhone || rawPhone === "+") {
            return toast.warn("Phone number is required");
        }
        if (!isValidPhoneNumber(rawPhone)) {
            return toast.warn("Please enter a valid phone number for the selected country");
        }

        if (!formData.full_address.trim()) return toast.warn("Address is required");

        try {
            setLoading(true);
            await henceforthApi.Store.add(formData);
            toast.success("Store added successfully!");
            navigate("/store");
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed to add store");
        } finally {
            setLoading(false);
        }
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div>
            <BreadCrumb pathNameDeclare={breadCrumbPath} />

            <div className="page-spacing">
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-lg-8 col-xl-7">
                            <div className="common-card">
                                <div className="common-card-title">
                                    <h5>Add New Store</h5>
                                </div>

                                <div className="common-card-content">
                                    <form onSubmit={handleSubmit}>
                                        <div className="row g-3">
                                            {/* Store Name */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold">
                                                    Store Name <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    className="form-control"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    placeholder="Enter store name"
                                                    required
                                                />
                                            </div>

                                            {/* Email */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold">
                                                    Email <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    placeholder="Enter email"
                                                    required
                                                />
                                            </div>

                                            {/* Password */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold">
                                                    Password <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                    placeholder="Enter password"
                                                    required
                                                />
                                            </div>

                                            {/* Company */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold">Company Name</label>
                                                <input
                                                    className="form-control"
                                                    name="company"
                                                    value={formData.company}
                                                    onChange={handleChange}
                                                    placeholder="Enter company name"
                                                />
                                            </div>

                                            {/* ── Phone with react-phone-input-2 ── */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold">
                                                    Phone Number <span className="text-danger">*</span>
                                                </label>
                                                <PhoneInput
                                                    // default country flag
                                                    disableDropdown={true}
                                                    value={`${formData.country_code}${formData.phone_number}`}
                                                    onChange={handlePhoneChange}
                                                    inputClass="form-control"
                                                    containerClass="w-100"
                                                    inputStyle={{ width: "100%" }}
                                                    placeholder="Enter phone number"
                                                />
                                                {/* Live validation hint */}
                                                {rawPhone && rawPhone !== "+" && !isValidPhoneNumber(rawPhone) && (
                                                    <small className="text-danger">
                                                        Invalid number for selected country
                                                    </small>
                                                )}
                                            </div>

                                            {/* Location Search */}
                                            <div className="col-12">
                                                <label className="form-label fw-semibold">
                                                    Search Location <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    ref={placeInputRef}
                                                    className="form-control"
                                                    placeholder="Search area, landmark, or full address..."
                                                />
                                                <small className="text-muted">
                                                    Google Places will auto-fill address details
                                                </small>
                                            </div>

                                            {/* Auto-filled address fields */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold">Country</label>
                                                <input
                                                    className="form-control bg-light"
                                                    value={formData.country}
                                                    readOnly
                                                />
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold">State</label>
                                                <input
                                                    className="form-control"
                                                    name="state"
                                                    value={formData.state}
                                                    onChange={handleChange}
                                                />
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold">City</label>
                                                <input
                                                    className="form-control"
                                                    name="city"
                                                    value={formData.city}
                                                    onChange={handleChange}
                                                />
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold">Pin Code</label>
                                                <input
                                                    className="form-control"
                                                    name="pin_code"
                                                    value={formData.pin_code}
                                                    onChange={handleChange}
                                                />
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold">
                                                    Apartment / Flat No.
                                                </label>
                                                <input
                                                    className="form-control"
                                                    name="apartment_number"
                                                    value={formData.apartment_number}
                                                    onChange={handleChange}
                                                    placeholder="Flat / Apartment number"
                                                />
                                            </div>

                                            <div className="col-12">
                                                <label className="form-label fw-semibold">Full Address</label>
                                                <textarea
                                                    className="form-control"
                                                    name="full_address"
                                                    value={formData.full_address}
                                                    onChange={handleChange}
                                                    rows={4}
                                                    placeholder="Full address will appear here"
                                                />
                                            </div>

                                            {/* Actions */}
                                            <div className="col-12 mt-4">
                                                <div className="d-flex gap-3">
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary flex-fill px-4 py-2"
                                                        onClick={() => navigate("/store")}
                                                    >
                                                        Cancel
                                                    </button>

                                                    <button
                                                        type="submit"
                                                        className="btn btn-theme flex-fill px-5 py-2"
                                                        disabled={loading}
                                                    >
                                                        {loading ? "Saving..." : "Add Store"}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddStore;